import { useCallback, useEffect, useRef, useState } from 'react';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { notificationContainer } from 'utils/notificationContainer';

export type TPgCallResult<T> = { ok: true; data: T } | { ok: false; error: unknown };

type TPgCallOptions = {
	/** Toast text when the gateway gives no `message`. */
	fallbackError: string;
	/** Do not toast errors: the caller shows them itself (e.g. a 409 with a special meaning). */
	silent?: boolean;
};

/**
 * One-off gateway call made from a component (previews, sync buttons, "Test on invoice"): keeps
 * its own loading flag and last result instead of a store slice. Data that lists show lives in
 * the `pgSettings` slice.
 */
export const usePgCall = <TArgs extends unknown[], TResult>(
	fn: (...args: TArgs) => Promise<TResult>,
	{ fallbackError, silent = false }: TPgCallOptions,
) => {
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<TResult | null>(null);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const run = useCallback(
		async (...args: TArgs): Promise<TPgCallResult<TResult>> => {
			setLoading(true);
			try {
				const data = await fn(...args);
				if (mounted.current) setResult(data);
				return { ok: true, data };
			} catch (error) {
				if (!silent) notificationContainer(getPgErrorMessage(error, fallbackError), 'error');
				return { ok: false, error };
			} finally {
				if (mounted.current) setLoading(false);
			}
		},
		[fn, fallbackError, silent],
	);

	const reset = useCallback(() => setResult(null), []);

	return { run, loading, result, reset };
};
